import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import PollCard from "../components/PollCard";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

const PublicProfilePage = () => {
  const { username } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [polls, setPolls] = useState([]);
  const [votes, setVotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("polls");
  const [followOverride, setFollowOverride] = useState(null);
  const [pollsRefreshKey, setPollsRefreshKey] = useState(0);
  const [followersOpen, setFollowersOpen] = useState(false);
  const [followingOpen, setFollowingOpen] = useState(false);
  const [followersList, setFollowersList] = useState([]);
  const [followingList, setFollowingList] = useState([]);
  const [followersLoading, setFollowersLoading] = useState(false);
  const [followingLoading, setFollowingLoading] = useState(false);

  useEffect(() => {
    const loadProfile = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/users/${username}`);
        setProfile(response.data);
      } catch (error) {
        toast.error("Unable to load profile.");
      } finally {
        setLoading(false);
      }
    };
    loadProfile();
  }, [username, user]);

  useEffect(() => {
    const loadPolls = async () => {
      try {
        const response = await api.get(`/users/${username}/polls`);
        setPolls(response.data);
      } catch (error) {
        toast.error("Unable to load polls.");
      }
    };
    loadPolls();
  }, [pollsRefreshKey, username, user]);

  useEffect(() => {
    const loadVotes = async () => {
      try {
        const response = await api.get(`/users/${username}/votes`);
        setVotes(response.data);
      } catch (error) {
        toast.error("Unable to load votes.");
      }
    };
    loadVotes();
  }, [username, user]);

  const pollCount = useMemo(() => profile?.poll_count || polls.length || 0, [polls.length, profile]);
  const followersCount = profile?.followers_count || 0;
  const followingCount = profile?.following_count || 0;
  const isOwnProfile = user?.id && profile?.id && user.id === profile.id;
  const isFollowing = followOverride !== null ? followOverride : profile?.is_following || false;

  const handleFollowToggle = async () => {
    if (!profile) return;
    const nextFollowing = !isFollowing;
    const delta = nextFollowing ? 1 : -1;
    if (!user) {
      setFollowOverride(nextFollowing);
      toast.error("Sign in to follow users.");
      setTimeout(() => {
        setFollowOverride(isFollowing);
      }, 800);
      return;
    }
    setFollowOverride(nextFollowing);
    setProfile((prev) => ({
      ...prev,
      followers_count: Math.max(0, (prev?.followers_count || 0) + delta),
      is_following: nextFollowing,
    }));
    try {
      if (isFollowing) {
        await api.delete(`/users/${profile.id}/follow`);
      } else {
        await api.post(`/users/${profile.id}/follow`);
      }
      const refreshed = await api.get(`/users/${profile.username}`);
      setProfile(refreshed.data);
      setFollowOverride(refreshed.data.is_following || false);
      setPollsRefreshKey((prev) => prev + 1);
      window.dispatchEvent(new Event("follow-state-changed"));
    } catch (error) {
      setFollowOverride(isFollowing);
      setProfile((prev) => ({
        ...prev,
        followers_count: Math.max(0, (prev?.followers_count || 0) - delta),
        is_following: isFollowing,
      }));
      toast.error("Unable to update follow status.");
    }
  };

  useEffect(() => {
    const loadFollowers = async () => {
      if (!profile) return;
      setFollowersLoading(true);
      try {
        const response = await api.get(`/users/${profile.id}/followers`);
        setFollowersList(response.data);
      } catch (error) {
        toast.error("Unable to load followers.");
      } finally {
        setFollowersLoading(false);
      }
    };
    if (followersOpen) {
      loadFollowers();
    }
  }, [followersOpen, profile]);

  useEffect(() => {
    const loadFollowing = async () => {
      if (!profile) return;
      setFollowingLoading(true);
      try {
        const response = await api.get(`/users/${profile.id}/following`);
        setFollowingList(response.data);
      } catch (error) {
        toast.error("Unable to load following.");
      } finally {
        setFollowingLoading(false);
      }
    };
    if (followingOpen) {
      loadFollowing();
    }
  }, [followingOpen, profile]);

  if (loading && !profile) {
    return (
      <div className="text-sm text-muted-foreground" data-testid="public-profile-loading">
        Loading profile...
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-sm text-muted-foreground" data-testid="public-profile-empty">
        Profile not found.
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="public-profile-page">
      <div className="rounded-2xl border border-border bg-card p-6 space-y-6" data-testid="public-profile-header">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="flex flex-wrap items-start gap-6">
            <img
              src={profile.avatar_url}
              alt={profile.username}
              className="h-36 w-36 rounded-full object-cover"
              data-testid="public-profile-avatar"
            />
            <div className="space-y-2">
              <div className="text-2xl font-semibold" data-testid="public-profile-fullname">
                {profile.full_name}
              </div>
              <div className="text-sm text-muted-foreground" data-testid="public-profile-username">
                @{profile.username}
              </div>
              <p className="text-sm leading-relaxed" data-testid="public-profile-bio">
                {profile.bio || "No bio yet."}
              </p>
            </div>
          </div>
          {!isOwnProfile && (
            <Button
              variant={isFollowing ? "secondary" : "default"}
              onClick={handleFollowToggle}
              className="min-w-[120px] self-start"
              data-testid="public-profile-follow-button"
            >
              {isFollowing ? "Following" : "Follow"}
            </Button>
          )}
        </div>
        <div className="flex flex-wrap gap-10 text-sm" data-testid="public-profile-counts">
          <div data-testid="public-profile-poll-count">Polls: {pollCount}</div>
          <button
            type="button"
            className="text-left"
            onClick={() => setFollowersOpen(true)}
            data-testid="public-profile-followers-count"
          >
            Followers: {followersCount}
          </button>
          <button
            type="button"
            className="text-left"
            onClick={() => setFollowingOpen(true)}
            data-testid="public-profile-following-count"
          >
            Following: {followingCount}
          </button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="public-profile-tabs">
        <TabsList className="grid w-full grid-cols-2 rounded-xl bg-muted/50 p-1" data-testid="public-profile-tabs-list">
          <TabsTrigger
            value="polls"
            className="rounded-lg data-[state=active]:bg-background"
            data-testid="public-profile-tab-polls"
          >
            Polls
          </TabsTrigger>
          <TabsTrigger
            value="votes"
            className="rounded-lg data-[state=active]:bg-background"
            data-testid="public-profile-tab-votes"
          >
            Votes
          </TabsTrigger>
        </TabsList>
        <TabsContent value="polls" className="space-y-7" data-testid="public-profile-polls">
          {polls.length === 0 ? (
            <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground" data-testid="public-profile-polls-empty">
              This user hasn’t created polls yet.
            </div>
          ) : (
            polls.map((poll) => <PollCard key={poll.id} poll={poll} />)
          )}
        </TabsContent>
        <TabsContent value="votes" className="space-y-7" data-testid="public-profile-votes">
          {votes.length === 0 ? (
            <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground" data-testid="public-profile-votes-empty">
              No public votes yet.
            </div>
          ) : (
            votes.map((poll) => <PollCard key={poll.id} poll={poll} />)
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={followersOpen} onOpenChange={setFollowersOpen}>
        <DialogContent className="max-w-md" data-testid="public-profile-followers-modal">
          <DialogHeader>
            <DialogTitle>Followers</DialogTitle>
            <DialogDescription data-testid="public-profile-followers-description">
              People who follow @{profile.username}
            </DialogDescription>
          </DialogHeader>
          {followersLoading ? (
            <div className="text-sm text-muted-foreground" data-testid="public-profile-followers-loading">
              Loading followers...
            </div>
          ) : followersList.length === 0 ? (
            <div className="text-sm text-muted-foreground" data-testid="public-profile-followers-empty">
              No followers yet.
            </div>
          ) : (
            <div className="space-y-3" data-testid="public-profile-followers-list">
              {followersList.map((follower) => (
                <button
                  key={follower.id}
                  type="button"
                  className="flex items-center gap-3 text-left"
                  onClick={() => {
                    navigate(`/user/${follower.username}`);
                    setFollowersOpen(false);
                  }}
                  data-testid={`public-profile-followers-item-${follower.id}`}
                >
                  <img
                    src={follower.avatar_url}
                    alt={follower.username}
                    className="h-9 w-9 rounded-full object-cover"
                  />
                  <div>
                    <div className="text-sm font-semibold">@{follower.username}</div>
                    <div className="text-xs text-muted-foreground">{follower.full_name}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={followingOpen} onOpenChange={setFollowingOpen}>
        <DialogContent className="max-w-md" data-testid="public-profile-following-modal">
          <DialogHeader>
            <DialogTitle>Following</DialogTitle>
            <DialogDescription data-testid="public-profile-following-description">
              People @{profile.username} follows
            </DialogDescription>
          </DialogHeader>
          {followingLoading ? (
            <div className="text-sm text-muted-foreground" data-testid="public-profile-following-loading">
              Loading following...
            </div>
          ) : followingList.length === 0 ? (
            <div className="text-sm text-muted-foreground" data-testid="public-profile-following-empty">
              Not following anyone yet.
            </div>
          ) : (
            <div className="space-y-3" data-testid="public-profile-following-list">
              {followingList.map((followed) => (
                <button
                  key={followed.id}
                  type="button"
                  className="flex items-center gap-3 text-left"
                  onClick={() => {
                    navigate(`/user/${followed.username}`);
                    setFollowingOpen(false);
                  }}
                  data-testid={`public-profile-following-item-${followed.id}`}
                >
                  <img
                    src={followed.avatar_url}
                    alt={followed.username}
                    className="h-9 w-9 rounded-full object-cover"
                  />
                  <div>
                    <div className="text-sm font-semibold">@{followed.username}</div>
                    <div className="text-xs text-muted-foreground">{followed.full_name}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublicProfilePage;
